import type { ReactNode } from 'react'
import {
  CustomerVisibilityField,
  customerVisibilityBlock,
  type MeetingDetail,
} from '@/entities/meeting'
import { formatMeetingStart } from '@/shared/lib'
import { ZoneNote } from '@/shared/ui/zone-note'
import { useSetCustomerVisibility } from '../model/use-set-customer-visibility'

// Аудит пометки: «кто это открыл клиенту» — вопрос, который задают первым,
// когда встреча оказалась видна снаружи. Имя резолвит бэк; пока его нет —
// показываем идентификатор, это честнее пустоты.
function auditNote(meeting: MeetingDetail): ReactNode {
  if (!meeting.customerVisible) return undefined
  const who = meeting.customerVisibleByName ?? meeting.customerVisibleBy
  const when = meeting.customerVisibleAt ? formatMeetingStart(meeting.customerVisibleAt) : undefined
  if (!who && !when) return undefined
  // Момент одиночный — зона стоит тегом сразу за ним: отметка аудита читается
  // и в отрыве от остального экрана.
  return (
    <>
      {`Открыл клиенту ${who ?? 'сотрудник'}`}
      {when && (
        <>
          {` · ${when} `}
          <ZoneNote />
        </>
      )}
    </>
  )
}

// Переключатель видимости встречи для клиента компании на карточке встречи.
export function CustomerVisibilityControl({ meeting }: { meeting: MeetingDetail }) {
  const mutation = useSetCustomerVisibility(meeting.id)
  const note = auditNote(meeting)
  // Снять пометку можно всегда: инварианты запрещают только включение.
  const block = meeting.customerVisible
    ? undefined
    : customerVisibilityBlock({ private: meeting.private, companyId: meeting.companyId })

  return (
    <CustomerVisibilityField
      checked={meeting.customerVisible}
      onChange={(visible) => mutation.mutate(visible)}
      companyName={meeting.companyName ?? meeting.companyId}
      busy={mutation.isPending}
      {...(block ? { block } : {})}
      {...(note ? { footnote: note } : {})}
    />
  )
}
